import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  isSameYear,
  getWeek,
  addWeeks,
  parseISO,
  isWithinInterval,
  setWeek,
  isBefore,
  isAfter,
  setYear,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import Skeleton from './Skeleton';
import { ChevronDown, Calendar, TrendingUp, BarChart3, PieChart } from 'lucide-react';

interface WeekTotal {
  cash: number;
  card: number;
  invoice: number;
  total: number;
}

export default function WeeksOverview() {
  const { year = '2025' } = useParams();
  const navigate = useNavigate();
  const [weekTotals, setWeekTotals] = useState<Record<number, WeekTotal>>({});
  const [loading, setLoading] = useState(true);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [activeYears, setActiveYears] = useState<number[]>([]);
  const [currentWeekStats, setCurrentWeekStats] = useState<WeekTotal>({ cash: 0, card: 0, invoice: 0, total: 0 });
  const [showTips, setShowTips] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: settings } = await supabase
          .from('settings')
          .select('active_years, show_tips')
          .eq('user_id', user.id)
          .maybeSingle();

        if (settings) {
          if (settings.active_years) {
            setActiveYears(settings.active_years);
          }
          setShowTips(settings.show_tips ?? true);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, []);

  const years = activeYears.sort((a, b) => a - b);
  const selectedYear = parseInt(year);
  const currentDate = new Date();
  
  // Use the same 7 AM cutoff logic as revenue entry for week calculation
  const adjustedDateForWeek = currentDate.getHours() < 7
    ? new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 1)
    : currentDate;
  const currentWeekNumber = getWeek(adjustedDateForWeek, { weekStartsOn: 1, firstWeekContainsDate: 4 });

  useEffect(() => {
    const fetchWeekTotals = async () => {
      setLoading(true);
      const yearStart = startOfYear(new Date(selectedYear, 0, 1));
      const yearEnd = endOfYear(new Date(selectedYear, 11, 31));

      const { data, error } = await supabase
        .from('revenue_entries')
        .select('*')
        .gte('date', format(yearStart, 'yyyy-MM-dd'))
        .lte('date', format(yearEnd, 'yyyy-MM-dd'));

      // Fetch tips if show_tips is enabled
      let tipsMap: Record<string, number> = {};
      if (showTips) {
        const { data: tipsData } = await supabase
          .from('hidden_revenue')
          .select('date, amount')
          .gte('date', format(yearStart, 'yyyy-MM-dd'))
          .lte('date', format(yearEnd, 'yyyy-MM-dd'));

        if (tipsData) {
          tipsData.forEach(tip => {
            if (!tipsMap[tip.date]) {
              tipsMap[tip.date] = 0;
            }
            tipsMap[tip.date] += tip.amount;
          });
        }
      }

      if (!error && data) {
        const totals: Record<number, WeekTotal> = {};

        data.forEach(entry => {
          const entryDate = parseISO(entry.date);
          const weekNumber = getWeek(entryDate, {
            weekStartsOn: 1,
            firstWeekContainsDate: 4
          });

          if (!totals[weekNumber]) {
            totals[weekNumber] = { cash: 0, card: 0, invoice: 0, total: 0 };
          }
          const tipsAmount = showTips ? (tipsMap[entry.date] || 0) : 0;
          totals[weekNumber].cash += entry.cash_revenue;
          totals[weekNumber].card += entry.card_revenue;
          totals[weekNumber].invoice += entry.invoice_revenue;
          totals[weekNumber].total += entry.cash_revenue + entry.card_revenue + entry.invoice_revenue + tipsAmount;
        });

        setWeekTotals(totals);
        
        // Set current week stats if we're viewing the current year
        if (selectedYear === currentDate.getFullYear() && totals[currentWeekNumber]) {
          setCurrentWeekStats(totals[currentWeekNumber]);
        } else {
          setCurrentWeekStats({ cash: 0, card: 0, invoice: 0, total: 0 });
        }
      }
      setLoading(false);
    };

    fetchWeekTotals();
  }, [selectedYear, currentWeekNumber, showTips]);

  const yearTotals = Object.values(weekTotals).reduce(
    (acc, week) => {
      acc.cash += week.cash;
      acc.card += week.card;
      acc.invoice += week.invoice;
      acc.total += week.total;
      return acc;
    },
    { cash: 0, card: 0, invoice: 0, total: 0 }
  );

  // Generate weeks for the selected year
  const weeks = [];
  const yearStart = startOfYear(new Date(selectedYear, 0, 1));
  const yearEnd = endOfYear(new Date(selectedYear, 11, 31));
  
  // If it's 2024, start from week 38 (September 16, 2024)
  let currentDateIterator = selectedYear === 2024
    ? new Date(2024, 8, 16) // September 16, 2024 (start of week 38)
    : startOfWeek(yearStart, { weekStartsOn: 1 });

  while (currentDateIterator <= yearEnd) {
    const weekStart = startOfWeek(currentDateIterator, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDateIterator, { weekStartsOn: 1 });
    
    // For 2024, only add weeks from week 38 onwards
    const weekNumber = getWeek(currentDateIterator, { 
      weekStartsOn: 1,
      firstWeekContainsDate: 4
    });
    
    if (isSameYear(weekEnd, new Date(selectedYear, 11, 31)) && 
        (selectedYear !== 2024 || weekNumber >= 38)) {
      const weekNumber = getWeek(currentDateIterator, { 
        weekStartsOn: 1,
        firstWeekContainsDate: 4
      });

      weeks.push({
        start: weekStart,
        end: weekEnd,
        number: weekNumber
      });
    }
    
    currentDateIterator = addWeeks(currentDateIterator, 1);
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-32" />
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <Skeleton className="h-5 w-32 mb-2" />
              <Skeleton className="h-8 w-24 mb-1" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>

        {/* Table Skeleton */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {[...Array(6)].map((_, i) => (
                    <th key={i} className="px-6 py-3">
                      <Skeleton className="h-4 w-24" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(52)].map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {[...Array(6)].map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <Skeleton className="h-6 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  {[...Array(6)].map((_, i) => (
                    <td key={i} className="px-6 py-4">
                      <Skeleton className="h-6 w-full" />
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Determine if we're viewing the current year and week
  const isCurrentYear = selectedYear === currentDate.getFullYear();
  const currentWeekText = isCurrentYear 
    ? `Week ${currentWeekNumber}` 
    : "Huidige week";

  return (
    <div className="space-y-6">
      {/* Header with Year Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Weekomzet {year}
        </h1>
        
        <div className="relative w-full lg:w-auto">
          <button
            onClick={() => setShowYearDropdown(!showYearDropdown)}
            className="w-full lg:w-auto flex items-center justify-between lg:justify-start space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span>Jaar: {selectedYear}</span>
            <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>

          {showYearDropdown && (
            <div className="absolute right-0 mt-2 w-full lg:w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
              <div className="py-1">
                {years.map((yearOption) => (
                  <button
                    key={yearOption}
                    onClick={() => {
                      navigate(`/weeks/${yearOption}`);
                      setShowYearDropdown(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm ${
                      yearOption === selectedYear
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {yearOption}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {currentWeekText}
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{currentWeekStats.total.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Totale omzet
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Contant
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{currentWeekStats.cash.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {((currentWeekStats.cash / currentWeekStats.total) * 100 || 0).toFixed(1)}% van totaal
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Pin
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{currentWeekStats.card.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {((currentWeekStats.card / currentWeekStats.total) * 100 || 0).toFixed(1)}% van totaal
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Rekening
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{currentWeekStats.invoice.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {((currentWeekStats.invoice / currentWeekStats.total) * 100 || 0).toFixed(1)}% van totaal
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Week
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Periode
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Contant
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Pin
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Rekening
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Totaal
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {weeks.map((week) => {
                const totals = weekTotals[week.number] || { cash: 0, card: 0, invoice: 0, total: 0 };
                const isCurrentWeek = isCurrentYear && week.number === currentWeekNumber;

                return (
                  <tr 
                    key={format(week.start, 'yyyy-MM-dd')} 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                      isCurrentWeek ? 'bg-green-50 dark:bg-green-900/10' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BarChart3 className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                        <span className={`text-sm font-medium ${
                          isCurrentWeek 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          Week {week.number}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {format(week.start, 'dd/MM')} - {format(week.end, 'dd/MM')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.cash.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.card.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.invoice.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-600 dark:text-blue-400">
                      €{totals.total.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                <td
                  colSpan={2}
                  className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-800 dark:text-blue-200"
                >
                  Jaartotalen
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{yearTotals.cash.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{yearTotals.card.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{yearTotals.invoice.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-blue-800 dark:text-blue-200">
                  €{yearTotals.total.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}